/** @format */

import { defineStore } from 'pinia'
import UnitTypeService from '@/api/UnitTypeService.js'
import {
	DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
	DEFAULT_RV_SHARE,
	RV_CURRENCY_DIGITS,
	RV_RESIDUAL_FLOOR,
} from '@/data/constants.js'
import { roundHalfUp } from '@/helpers/roundHalfUp.js'

const RESIDUAL_VALUE_KEYS = ['residual_value_day_1', 'residual_value_1_year']

const RVT_INPUT_KEYS = ['value', 'residual_value_day_1', 'residual_value_1_year', 'rv_depreciation_period_years']

const RVT_OVERRIDE_KEYS = ['residual_value_day_1', 'residual_value_1_year', 'rv_depreciation_period_years']

const ROW_EDIT_TOLERANCE = 0.5 / 10 ** RV_CURRENCY_DIGITS

export function rvtSignatureOf(data) {
	return RVT_INPUT_KEYS.map((key) => data[key])
		.map((value) => (value == null || value === '' ? '' : Number(value)))
		.join('|')
}

function fullUtilizationKey(rvt) {
	return Object.keys(rvt || {}).find((key) => Number(key.replace('-', '.')) === 1) ?? null
}

function fullUtilizationRow(rvt) {
	const key = fullUtilizationKey(rvt)
	return key ? rvt[key] : null
}

function hasRows(rvt) {
	return !!rvt && Object.keys(rvt).length > 0
}

function isValidPrice(price) {
	return price >= 1
}

function emptyRvAmounts() {
	return { residual_value_day_1: null, residual_value_1_year: null, rows: {} }
}

function scaledRowAmount({ share, amount }, price) {
	if (amount <= RV_RESIDUAL_FLOOR) {
		return amount
	}
	return Math.max(RV_RESIDUAL_FLOOR, roundHalfUp(share * price, RV_CURRENCY_DIGITS))
}

export const useUnitTypeStore = defineStore('unitType', {
	state: () => ({
		unitTypeData: {
			id: null,
			name: '',
			identifier: null,
			customization_degree: null,
			is_stationary: '1',

			value: null,
			refurbishment: null,
			buy_back: 0,
			allow_delete: false,
			planned_usage_per_a: null,
			maximum_usage_per_a: null,
			billing_units: [],
			residual_value_day_1: null,
			residual_value_1_year: null,
			rv_depreciation_period_years: DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
			residual_values_table: null,
			currency_code: 'EUR',
			model_year: null,
		},

		originalData: null,
		isDirty: false,
		isCloned: false,

		isResidualValue1YearLocked: true,

		defaultRvShare: DEFAULT_RV_SHARE,
		isDefaultRvShareLoaded: false,

		rvtInputsSignature: null,
		rvPercentages: emptyRvAmounts(),
		rvLastWritten: emptyRvAmounts(),
		rvtBaselineRows: null,
		rvScaledFromValue: null,
		rvtRevision: 0,
		rvtSession: 0,
		rvtPendingRecalculation: null,

		isValidBasicInfo: false,
		isValidPurchaseInfo: false,
		isValidUtilizationInfo: false,
		isValidBuyBackOption: false,
		isValidResidualValueInputs: false,

		loading: false,
		saving: false,
		calculating: false,
		deleting: false,
	}),

	getters: {
		hasRvt(state) {
			return hasRows(state.unitTypeData.residual_values_table)
		},

		isRvtCurrent(state) {
			return (
				hasRows(state.unitTypeData.residual_values_table) &&
				state.rvtInputsSignature === rvtSignatureOf(state.unitTypeData)
			)
		},

		rvtEditedSinceBaseline(state) {
			const baseline = state.rvtBaselineRows
			const row = fullUtilizationRow(state.unitTypeData.residual_values_table)
			if (!baseline || !row) {
				return false
			}
			const price = Number(state.unitTypeData.value) || 0
			return Object.entries(row).some(([months, value]) => {
				if (Number(months) === 1 || Number(months) === 12) {
					return false
				}
				const entry = baseline[months]
				return !entry || Math.abs(Number(value) - scaledRowAmount(entry, price)) > ROW_EDIT_TOLERANCE
			})
		},
	},

	actions: {
		notification(severity, detail) {
			const summary = severity === 'error' ? 'Error' : 'Success'
			this.toast.add({
				severity,
				summary,
				detail,
				life: this.getToastDuration(detail, severity),
			})
		},

		resetState() {
			this.unitTypeData = {
				id: null,
				name: '',
				identifier: null,
				customization_degree: null,
				is_stationary: '1',

				value: null,
				refurbishment: null,
				buy_back: 0,
				allow_delete: false,
				planned_usage_per_a: null,
				maximum_usage_per_a: null,
				billing_units: [],
				residual_value_day_1: null,
				residual_value_1_year: null,
				rv_depreciation_period_years: DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
				residual_values_table: null,
				currency_code: 'EUR',
				model_year: null,
			}
			this.originalData = null
			this.isDirty = false
			this.isResidualValue1YearLocked = true
			this.defaultRvShare = DEFAULT_RV_SHARE
			this.isDefaultRvShareLoaded = false
			this.rvtInputsSignature = null
			this.rvPercentages = emptyRvAmounts()
			this.rvLastWritten = emptyRvAmounts()
			this.rvtBaselineRows = null
			this.rvScaledFromValue = null
			this.rvtSession += 1
			this.calculating = false
			this.rvtPendingRecalculation = null
			this.isValidBasicInfo = false
			this.isValidPurchaseInfo = false
			this.isValidUtilizationInfo = false
			this.isValidBuyBackOption = false
			this.isValidResidualValueInputs = false
		},

		setBuyBackOption() {
			if (
				this.unitTypeData.residual_value_1_year !== null ||
				this.unitTypeData.residual_value_day_1 !== null
			) {
				this.unitTypeData.buy_back = 1
			}
		},

		setValue(value) {
			const previous = Number(this.unitTypeData.value) || 0
			const next = Number(value) || 0
			this.unitTypeData.value = value

			if (previous === next) {
				return
			}

			const shareBase = isValidPrice(previous) ? previous : next
			let changed = false

			RESIDUAL_VALUE_KEYS.forEach((key) => {
				const current = this.unitTypeData[key]
				if (current == null || current === '') {
					return
				}
				const amount = Number(current)
				if (this.rvPercentages[key] == null || amount !== this.rvLastWritten[key]) {
					if (!isValidPrice(shareBase)) {
						return
					}
					this.rvPercentages[key] = amount / shareBase
				}
				const scaled = isValidPrice(next)
					? Math.min(roundHalfUp(this.rvPercentages[key] * next, RV_CURRENCY_DIGITS), next)
					: 0
				changed = changed || scaled !== amount
				this.unitTypeData[key] = scaled
				this.rvLastWritten[key] = scaled
			})

			const table = this.unitTypeData.residual_values_table
			const rowKey = fullUtilizationKey(table)
			if (rowKey && isValidPrice(next)) {
				const scaledRow = {}
				Object.entries(table[rowKey]).forEach(([months, value]) => {
					const amount = Number(value)
					const lastWritten = this.rvLastWritten.rows[months]
					if (
						!this.rvPercentages.rows[months] ||
						lastWritten == null ||
						Math.abs(amount - lastWritten) > ROW_EDIT_TOLERANCE
					) {
						this.rvPercentages.rows[months] = { share: amount / shareBase, amount }
					}
					const scaled = scaledRowAmount(this.rvPercentages.rows[months], next)
					changed = changed || scaled !== amount
					scaledRow[months] = scaled
					this.rvLastWritten.rows[months] = scaled
				})
				this.unitTypeData.residual_values_table = { ...table, [rowKey]: scaledRow }
			}

			if (hasRows(table)) {
				this.rvtInputsSignature = rvtSignatureOf(this.unitTypeData)
				this.rvtRevision += 1
			}

			if (changed && this.rvScaledFromValue === null && isValidPrice(previous)) {
				this.rvScaledFromValue = previous
			}
			if (next === this.rvScaledFromValue) {
				this.rvScaledFromValue = null
			}
		},

		rememberRvShares(keys) {
			const price = Number(this.unitTypeData.value) || 0
			if (!isValidPrice(price)) {
				return
			}
			keys.forEach((key) => {
				const amount = this.unitTypeData[key]
				const isEmpty = amount == null || amount === ''
				this.rvPercentages[key] = isEmpty ? null : Number(amount) / price
				this.rvLastWritten[key] = isEmpty ? null : Number(amount)
			})
		},

		rememberRvPercentages() {
			const price = Number(this.unitTypeData.value) || 0
			if (!isValidPrice(price)) {
				return
			}
			this.rememberRvShares(RESIDUAL_VALUE_KEYS)

			const shares = {}
			const amounts = {}
			const row = fullUtilizationRow(this.unitTypeData.residual_values_table)
			Object.entries(row || {}).forEach(([months, value]) => {
				const amount = Number(value)
				shares[months] = { share: amount / price, amount }
				amounts[months] = amount
			})
			this.rvPercentages.rows = shares
			this.rvLastWritten.rows = amounts
			this.rvtBaselineRows = row ? { ...shares } : null
		},

		clearResidualValues() {
			this.rvtSession += 1
			this.calculating = false
			this.unitTypeData.residual_value_day_1 = null
			this.unitTypeData.residual_value_1_year = null
			this.unitTypeData.rv_depreciation_period_years = DEFAULT_RV_DEPRECIATION_PERIOD_YEARS
			this.unitTypeData.residual_values_table = null
			this.rvPercentages = emptyRvAmounts()
			this.rvLastWritten = emptyRvAmounts()
			this.rvtBaselineRows = null
			this.rvtInputsSignature = null
			this.rvScaledFromValue = null
			this.isResidualValue1YearLocked = true
			this.rvtPendingRecalculation = null
		},

		async loadDefaultRvShare() {
			if (this.isDefaultRvShareLoaded) {
				return
			}
			try {
				const share = Number(await UnitTypeService.getDefaultRvShare())
				if (share > 0 && share <= 1) {
					this.defaultRvShare = share
				}
			} catch (error) {
				console.error(
					'Error fetching the default residual value share (stores/unitType/loadDefaultRvShare):',
					error,
				)
			} finally {
				this.isDefaultRvShareLoaded = true
			}
		},

		async getUnitTypeData(type) {
			this.rvtSession += 1
			this.calculating = false
			this.rvtPendingRecalculation = null
			this.loading = true
			try {
				const data = await UnitTypeService.get(type)
				Object.keys(data).forEach((key) => {
					if (this.unitTypeData[key] !== undefined) {
						this.unitTypeData[key] = data[key]
					}
				})
				this.unitTypeData.residual_values_table = data.residual_values_table ?? null
				this.setBuyBackOption()

				this.isResidualValue1YearLocked =
					Number(this.unitTypeData.residual_value_day_1) ===
					Number(this.unitTypeData.residual_value_1_year)

				this.rvtInputsSignature = this.hasRvt ? rvtSignatureOf(this.unitTypeData) : null
				this.rememberRvPercentages()
				this.rvtRevision += 1

				this.originalData = JSON.parse(JSON.stringify(this.unitTypeData))
				this.isDirty = false
			} catch (error) {
				console.error(
					'Error fetching unit type data (stores/unitType/getUnitTypeData):',
					error,
				)
			} finally {
				this.loading = false
			}
		},

		async create(fromCafModal = false) {
			this.saving = true
			try {
				if (this.unitTypeData.buy_back && !this.isRvtCurrent) {
					this.notification('error', '§ui_message.create_failed')
					return
				}

				const { data } = await UnitTypeService.create(this.unitTypeData, {
					...this.unitTypeData.residual_values_table,
				})

				this.unitTypeData.id = data.unit_type_id
				if (!fromCafModal) {
					this.router.push({ name: 'UnitTypeShow', params: { id: this.unitTypeData.id } })
				}
				this.isCloned = false
				this.rvScaledFromValue = null
				this.originalData = JSON.parse(JSON.stringify(this.unitTypeData))
				this.isDirty = false
				this.notification('success', '§ui_message.create_successful')
			} catch (error) {
				console.error('Error creating unit type data (stores/unitType/create):', error)
			} finally {
				this.saving = false
			}
		},

		async update() {
			this.saving = true
			try {
				if (this.unitTypeData.buy_back && !this.isRvtCurrent) {
					this.notification('error', '§ui_message.update_failed')
					return
				}

				await UnitTypeService.update({ type: this.unitTypeData.id }, this.unitTypeData, {
					...this.unitTypeData.residual_values_table,
				})

				this.rvScaledFromValue = null
				this.originalData = JSON.parse(JSON.stringify(this.unitTypeData))
				this.isDirty = false
				this.notification('success', '§ui_message.update_successful')
			} catch (error) {
				console.error('Error updating unit type data (stores/unitType/update):', error)
			} finally {
				this.saving = false
			}
		},

		async delete() {
			this.deleting = true
			try {
				await UnitTypeService.delete({ type: this.unitTypeData.id })
				this.router.push({ name: 'UnitTypeIndex' })
				this.notification('success', '§ui_message.delete_successful')
			} catch (error) {
				console.error('Error deleting unit type data (stores/unitType/delete):', error)
			} finally {
				this.deleting = false
			}
		},

		clone(cloneRVT = false) {
			this.unitTypeData.id = null
			if (!cloneRVT) {
				this.unitTypeData.buy_back = 0
				this.clearResidualValues()
			}
			this.originalData = null
			this.isDirty = true
			this.isCloned = true
			this.router.push({ name: 'UnitTypeCreate' })
		},

		recalculateRvtFromTable({ period } = {}) {
			const row = fullUtilizationRow(this.unitTypeData.residual_values_table) || {}
			const yearOne = row['12'] ?? null
			const dayOne = row['1'] ?? yearOne
			const overrides = {
				residual_value_day_1: dayOne === null ? null : Number(dayOne),
				residual_value_1_year: yearOne === null ? null : Number(yearOne),
			}
			if (period != null) {
				overrides.rv_depreciation_period_years = period
			}
			return this.calculateRVT(overrides)
		},

		async calculateRVT(overrides = {}) {
			const session = this.rvtSession
			const payload = { ...this.unitTypeData, ...overrides }
			this.calculating = true
			try {
				const { data } = await UnitTypeService.calculateRVT(payload)
				if (session !== this.rvtSession) {
					return null
				}
				if ('residual_value_day_1' in overrides && 'residual_value_1_year' in overrides) {
					this.isResidualValue1YearLocked =
						Number(overrides.residual_value_day_1) === Number(overrides.residual_value_1_year)
				}
				RVT_OVERRIDE_KEYS.forEach((key) => {
					this.unitTypeData[key] = payload[key]
				})
				this.unitTypeData.residual_values_table = data.residual_values_table
				this.rvtInputsSignature = rvtSignatureOf(payload)
				this.rememberRvPercentages()
				this.rvScaledFromValue = null
				this.rvtRevision += 1
				return data.residual_values_table
			} catch (error) {
				if (session === this.rvtSession) {
					console.error('Error calculating RVT (stores/unitType/calculateRVT):', error)
				}
				return null
			} finally {
				if (session === this.rvtSession) {
					this.calculating = false
				}
			}
		},
	},
})
